<script lang="ts">
    import { convertBio } from '$lib/collectui';
    import type { Curator } from '$lib/collectui/types';
    import { supabase } from '$lib/supabase';
    import { Loader, Trash2 } from 'lucide-svelte';
    import { toast } from 'svelte-sonner';
    import { fade } from 'svelte/transition';

    const {
        curator,
        ondelete,
    }: {
        curator: Curator;
        ondelete?: () => void;
    } = $props();

    let showDeleteConfirm = $state(false);
    let deleting = $state(false);

    async function handleDelete() {
        deleting = true;

        const { error } = await supabase
            .from('collectui_curators')
            .delete()
            .eq('id', curator.id);
        if (error) {
            throw error;
        }

        deleting = false;
        showDeleteConfirm = false;
        ondelete?.();
        toast.success('Curators successfully removed');
    }
</script>

<div
    data-username={curator.username}
    class="relative group flex h-full flex-col overflow-hidden rounded-xl bg-white dark:bg-zinc-900 shadow outline outline-offset-[-1px] outline-black/5 dark:outline-white/5">
    <img
        class="h-[140px] border-b border-black/5 dark:border-white/5 object-cover"
        src={curator.profile_banner_url}
        alt="" />
    <div class="flex flex-col items-start justify-start gap-3 p-4 pt-10">
        <div class="flex flex-col items-start justify-start gap-0.5">
            <div class="inline-flex items-center justify-start gap-0.5">
                <a
                    href="https://x.com/{curator.username}"
                    target="_blank"
                    class="font-geist justify-start text-xl font-semibold text-zinc-900 dark:text-zinc-100 hover:underline">
                    {curator.name}
                </a>
                {#if curator.verified}
                    <img
                        src="/images/verified.svg"
                        alt=""
                        class="h-3.5 w-3.5" />
                {/if}
            </div>
            <div
                class="font-geist justify-start text-sm font-medium text-[#71717b] dark:text-zinc-400">
                @{curator.username}
            </div>
        </div>
        <div class="justify-start">
            <span
                class="font-geist text-sm font-medium text-wrap text-zinc-900 dark:text-zinc-100">
                {@html convertBio(curator.bio, curator.entities)}
            </span>
        </div>
    </div>
    <div
        class="absolute top-[102px] left-[24px] h-16 w-16 overflow-hidden rounded-[133.33px] outline-[1.33px] outline-offset-[-1.33px] outline-black/5 dark:outline-white/5">
        <img class="h-16 w-16" src={curator.profile_image_url} alt="" />
    </div>

    {#if ondelete}
        <button
            onclick={() => (showDeleteConfirm = true)}
            class=" absolute hidden right-2 opacity-50 hover:opacity-100 bottom-2 bg-white/5 group-hover:flex items-center justify-center p-2 text-red-500 hover:bg-white/2 rounded-full border-1 border-white/5">
            <Trash2 class="size-4" />
        </button>

        {#if showDeleteConfirm}
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
                transition:fade={{ duration: 200 }}
                onmouseleave={() => (showDeleteConfirm = false)}
                class="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center px-2">
                {#if deleting}
                    <Loader class="animate-spin" />
                {:else}
                    <div
                        class="bg-white dark:bg-zinc-900 p-4 rounded-lg shadow-lg">
                        <p class="text-sm font-medium mb-4">
                            Are you sure you want to delete this curator?
                        </p>
                        <div class="flex gap-2 justify-end">
                            <button
                                class="px-3 py-1.5 text-sm rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                                onclick={() => (showDeleteConfirm = false)}>
                                Cancel
                            </button>
                            <button
                                class="px-3 py-1.5 text-sm rounded-md bg-red-500 text-white hover:bg-red-600"
                                onclick={handleDelete}>
                                Delete
                            </button>
                        </div>
                    </div>
                {/if}
            </div>
        {/if}
    {/if}
</div>
